import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    FileText,
    FileSignature,
    Plus,
    ShieldCheck,
    BarChart3,
    Building2,
    LogOut,
    User as UserIcon,
    Calendar as CalendarIcon,
    Radar,
    Bell,
    Inbox,
    Sparkles,
    TrendingDown,
    Layers,
    Award,
    FolderTree,
    Mailbox,
    Scale,
    ClipboardCheck,
    Star,
    BookText,
    Briefcase,
    Gift,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { listWatchlistAlerts, listCompetitorAlerts, trackingOverdueCount, createDemoTender } from "@/lib/api";
import { startWelcomeTour } from "@/components/WelcomeTour";

export const Layout = ({ children }) => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [unread, setUnread] = React.useState(0);

    // Permissions helper — true if the current user can access the given module key.
    const canAccess = React.useCallback(
        (module) => {
            if (!user) return false;
            if (user.is_admin) return true;
            const perms = user.permissions || ["*"];
            if (perms.includes("*")) return true;
            if (user.company_role === "admin") return true;
            return perms.includes(module);
        },
        [user]
    );
    const isCompanyAdmin =
        !!user && (user.is_admin || user.company_role === "admin");

    React.useEffect(() => {
        if (!user) return;
        let cancelled = false;
        const fetch = async () => {
            try {
                const [wl, cmp, tr] = await Promise.all([
                    listWatchlistAlerts(true),
                    listCompetitorAlerts(true),
                    trackingOverdueCount().catch(() => ({ count: 0 })),
                ]);
                if (!cancelled) {
                    setUnread(
                        (wl.unread_count || 0) +
                            (cmp.unread_count || 0) +
                            (tr.count || 0),
                    );
                }
            } catch {
                /* ignore */
            }
        };
        fetch();
        const id = setInterval(fetch, 60000); // refresh every 1 min
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [user]);

    return (
        <div className="min-h-screen flex bg-[#f6f8ff]" data-testid="app-layout">
            <aside
                className="hidden md:flex w-64 shrink-0 flex-col border-r border-ink-100 bg-white"
                data-testid="sidebar"
            >
                <Link
                    to="/app"
                    className="h-16 flex items-center gap-2.5 px-6 border-b border-ink-100"
                    data-testid="sidebar-logo"
                >
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-600 to-cyan-400 text-white flex items-center justify-center shadow-pop">
                        <ShieldCheck size={17} strokeWidth={2.4} />
                    </div>
                    <span className="font-display text-xl font-extrabold tracking-tight text-ink-950">
                        LicitaIA
                    </span>
                </Link>
                <nav className="p-4 space-y-1 flex-1">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-ink-400 px-3 pt-2 pb-2">
                        Trabajo
                    </div>
                    <NavLink
                        to="/app"
                        end
                        className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                        data-testid="nav-dashboard"
                    >
                        <LayoutDashboard size={17} />
                        Centro de mando
                    </NavLink>
                    {canAccess("crm") && (
                        <NavLink
                            to="/app/crm"
                            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            data-testid="nav-crm"
                        >
                            <Briefcase size={17} />
                            CRM de licitaciones
                        </NavLink>
                    )}
                    {canAccess("licitaciones") && (
                        <NavLink
                            to="/app/licitaciones"
                            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            data-testid="nav-licitaciones"
                        >
                            <Layers size={17} />
                            Licitaciones
                        </NavLink>
                    )}
                    {canAccess("licitaciones") && (
                        <NavLink
                            to="/app/nueva"
                            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            data-testid="nav-new-tender"
                        >
                            <Plus size={17} />
                            Nueva licitación
                        </NavLink>
                    )}
                    {canAccess("seguimiento") && (
                        <NavLink
                            to="/app/seguimiento"
                            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            data-testid="nav-seguimiento"
                        >
                            <ClipboardCheck size={17} />
                            Seguimiento
                        </NavLink>
                    )}
                    {canAccess("calendario") && (
                        <NavLink
                            to="/app/calendario"
                            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            data-testid="nav-calendar"
                        >
                            <CalendarIcon size={17} />
                            Calendario
                        </NavLink>
                    )}
                    {canAccess("comunicaciones") && (
                        <NavLink
                            to="/app/comunicaciones"
                            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            data-testid="nav-comunicaciones"
                        >
                            <Mailbox size={17} />
                            Comunicaciones
                        </NavLink>
                    )}
                    {canAccess("buscar-plcsp") && (
                        <NavLink
                            to="/app/buscar-plcsp"
                            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            data-testid="nav-plcsp"
                        >
                            <Radar size={17} />
                            Buscador PLCSP
                        </NavLink>
                    )}
                    {canAccess("buscar-plcsp") && (
                        <NavLink
                            to="/app/busquedas-guardadas"
                            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            data-testid="nav-busquedas-guardadas"
                        >
                            <Star size={17} />
                            Mis búsquedas
                        </NavLink>
                    )}
                    {canAccess("cumplimentar-anexos") && (
                        <NavLink
                            to="/app/cumplimentar-anexos"
                            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            data-testid="nav-cumplimentar-anexos"
                        >
                            <FileSignature size={17} />
                            Cumplimentar anexos
                        </NavLink>
                    )}
                    {canAccess("memoria-tecnica") && (
                        <NavLink
                            to="/app/memoria-tecnica"
                            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            data-testid="nav-memoria-tecnica"
                        >
                            <BookText size={17} />
                            Memoria técnica
                        </NavLink>
                    )}
                    {canAccess("solvencia") && (
                        <NavLink
                            to="/app/solvencia"
                            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            data-testid="nav-solvencia"
                        >
                            <Award size={17} />
                            Solvencia
                        </NavLink>
                    )}
                    {canAccess("legal") && (
                        <NavLink
                            to="/app/legal"
                            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            data-testid="nav-legal"
                        >
                            <Scale size={17} />
                            Auxiliar Legal
                        </NavLink>
                    )}
                    {canAccess("mi-documentacion") && (
                        <NavLink
                            to="/app/mi-documentacion"
                            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            data-testid="nav-mi-documentacion"
                        >
                            <FolderTree size={17} />
                            Mi documentación
                        </NavLink>
                    )}
                    {canAccess("competidores") && (
                        <NavLink
                            to="/app/competidores"
                            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            data-testid="nav-competidores"
                        >
                            <TrendingDown size={17} />
                            Competidores
                        </NavLink>
                    )}
                    {canAccess("alertas") && (
                        <NavLink
                            to="/app/alertas"
                            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            data-testid="nav-alerts"
                        >
                            <Bell size={17} />
                            <span className="flex-1">Alertas</span>
                            {unread > 0 && (
                                <span
                                    className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center"
                                    data-testid="alerts-badge-sidebar"
                                >
                                    {unread > 99 ? "99+" : unread}
                                </span>
                            )}
                        </NavLink>
                    )}
                    {canAccess("analiticas") && (
                        <NavLink
                            to="/app/analiticas"
                            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            data-testid="nav-analytics"
                        >
                            <BarChart3 size={17} />
                            Analíticas
                        </NavLink>
                    )}

                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-ink-400 px-3 pt-6 pb-2">
                        Configuración
                    </div>
                    {canAccess("perfil-empresa") && (
                        <NavLink
                            to="/app/perfil-empresa"
                            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            data-testid="nav-company-profile"
                        >
                            <Building2 size={17} />
                            Perfil de empresa
                        </NavLink>
                    )}
                    {user?.is_admin && (
                        <NavLink
                            to="/app/clientes"
                            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            data-testid="nav-customers"
                        >
                            <Building2 size={17} />
                            Clientes
                        </NavLink>
                    )}
                    {user?.is_admin && (
                        <NavLink
                            to="/app/visitas"
                            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            data-testid="nav-visitas"
                        >
                            <Radar size={17} />
                            Visitas en vivo
                        </NavLink>
                    )}
                    {user?.is_admin && (
                        <NavLink
                            to="/app/cobertura"
                            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            data-testid="nav-cobertura"
                        >
                            <Layers size={17} />
                            Cobertura plataformas
                        </NavLink>
                    )}
                    {user?.is_admin && (
                        <NavLink
                            to="/app/contactos"
                            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            data-testid="nav-contacts"
                        >
                            <Inbox size={17} />
                            Solicitudes demo
                        </NavLink>
                    )}
                    {isCompanyAdmin && (
                        <NavLink
                            to="/app/usuarios"
                            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            data-testid="nav-users"
                        >
                            <UserIcon size={17} />
                            Usuarios
                        </NavLink>
                    )}
                    {user?.is_admin && (
                        <NavLink
                            to="/app/seguridad"
                            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            data-testid="nav-seguridad"
                        >
                            <ShieldCheck size={17} />
                            Seguridad
                        </NavLink>
                    )}
                    {user?.is_admin && (
                        <NavLink
                            to="/app/auditoria"
                            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            data-testid="nav-auditoria"
                        >
                            <ShieldCheck size={17} />
                            Auditoría
                        </NavLink>
                    )}

                    <div className="mt-6 mx-1 p-4 rounded-2xl bg-gradient-to-br from-brand-600 to-cyan-500 text-white relative overflow-hidden">
                        <div className="absolute inset-0 bg-grid-brand opacity-20" />
                        <div className="relative">
                            <Sparkles size={18} className="mb-2" />
                            <div className="font-display font-bold text-sm leading-tight mb-1">
                                IA Claude 4.5
                            </div>
                            <div className="text-[11px] text-white/80 leading-relaxed">
                                Análisis de pliegos en menos de 3 minutos.
                            </div>
                        </div>
                    </div>
                </nav>
                <div className="p-3 border-t border-ink-100">
                    {user && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    className="flex items-center gap-2.5 p-2 w-full hover:bg-brand-50 rounded-xl transition-colors"
                                    data-testid="user-menu-trigger"
                                >
                                    {user.picture ? (
                                        <img
                                            src={user.picture}
                                            alt=""
                                            className="w-9 h-9 rounded-xl"
                                            referrerPolicy="no-referrer"
                                        />
                                    ) : (
                                        <div className="w-9 h-9 bg-gradient-to-br from-brand-600 to-cyan-400 text-white flex items-center justify-center rounded-xl text-xs font-bold">
                                            {(user.name || user.email).slice(0, 2).toUpperCase()}
                                        </div>
                                    )}
                                    <div className="min-w-0 text-left">
                                        <div className="text-sm font-bold text-ink-950 truncate">
                                            {user.name || user.email}
                                        </div>
                                        <div className="text-xs text-ink-400 truncate font-medium">
                                            {user.email}
                                        </div>
                                    </div>
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="rounded-xl w-56" align="end">
                                <DropdownMenuLabel>Sesión</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => navigate("/app/perfil-empresa")}>
                                    <Building2 size={14} className="mr-2" />
                                    Perfil de empresa
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={() => {
                                        startWelcomeTour(navigate);
                                    }}
                                    data-testid="restart-welcome-tour"
                                >
                                    <Sparkles size={14} className="mr-2" />
                                    Volver a ver el tour
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={async () => {
                                        try {
                                            toast.loading(
                                                "Creando tu licitación demo…",
                                                { id: "demo-create" },
                                            );
                                            const t = await createDemoTender();
                                            toast.success(
                                                "Licitación demo lista",
                                                {
                                                    id: "demo-create",
                                                    duration: 5000,
                                                },
                                            );
                                            navigate(`/app/licitacion/${t.id}`);
                                        } catch (e) {
                                            toast.error(
                                                e?.response?.data?.detail ||
                                                    "No se pudo crear la demo.",
                                                { id: "demo-create" },
                                            );
                                        }
                                    }}
                                    data-testid="create-demo-tender"
                                >
                                    <Gift size={14} className="mr-2" />
                                    Crear licitación demo
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={logout}
                                    className="text-red-600"
                                    data-testid="logout-btn"
                                >
                                    <LogOut size={14} className="mr-2" />
                                    Cerrar sesión
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
            </aside>

            <div className="flex-1 min-w-0 flex flex-col">
                <header
                    className="h-16 shrink-0 border-b border-ink-100 bg-white/75 backdrop-blur-xl px-6 flex items-center justify-between sticky top-0 z-10"
                    data-testid="topbar"
                >
                    <Link
                        to="/app"
                        className="md:hidden flex items-center gap-2"
                    >
                        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-brand-600 to-cyan-400 text-white flex items-center justify-center">
                            <ShieldCheck size={16} />
                        </div>
                        <span className="font-display text-lg font-extrabold">LicitaIA</span>
                    </Link>
                    <div className="hidden md:flex items-center gap-2 text-xs">
                        <FileText size={14} className="text-brand-600" />
                        <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-ink-400">
                            Centro de mando · Licitaciones públicas
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => navigate("/app/alertas")}
                            className="relative w-10 h-10 rounded-full bg-white border border-ink-100 hover:bg-brand-50 hover:border-brand-200 flex items-center justify-center transition-all"
                            data-testid="topbar-bell"
                            title="Alertas PLCSP"
                        >
                            <Bell size={16} className="text-ink-700" />
                            {unread > 0 && (
                                <span
                                    className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-4.5 px-1 flex items-center justify-center"
                                    data-testid="topbar-bell-badge"
                                >
                                    {unread > 9 ? "9+" : unread}
                                </span>
                            )}
                        </button>
                        <Button
                            size="sm"
                            className="rounded-full bg-brand-600 hover:bg-brand-700 text-white px-5 h-10 shadow-pop hover:scale-[1.03] transition-all font-bold"
                            onClick={() => navigate("/app/nueva")}
                            data-testid="topbar-new-tender-btn"
                        >
                            <Plus size={15} className="mr-1" />
                            Nueva
                        </Button>
                        {/* Mobile: user avatar */}
                        {user && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button
                                        className="md:hidden w-10 h-10 rounded-xl bg-gradient-to-br from-brand-600 to-cyan-400 text-white flex items-center justify-center"
                                        data-testid="mobile-user-menu"
                                    >
                                        <UserIcon size={15} />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="rounded-xl w-56" align="end">
                                    <DropdownMenuItem onClick={() => navigate("/app")}>
                                        <LayoutDashboard size={14} className="mr-2" /> Panel
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate("/app/analiticas")}>
                                        <BarChart3 size={14} className="mr-2" /> Analíticas
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate("/app/perfil-empresa")}>
                                        <Building2 size={14} className="mr-2" /> Perfil empresa
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={logout} className="text-red-600">
                                        <LogOut size={14} className="mr-2" /> Salir
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                    </div>
                </header>

                <main className="flex-1 min-w-0">{children}</main>
            </div>
        </div>
    );
};

export default Layout;
